import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../redux/slices/userSlice';
import { setSearchQuery } from '../redux/slices/contentSlice';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import SearchAutocomplete from './SearchAutocomplete';

const HeaderContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 70px;
  padding: 0 4%;
  background: ${props => props.$scrolled ?
    'rgba(20, 20, 20, 0.95)' :
    'linear-gradient(180deg, rgba(0,0,0,0.7) 10%, transparent)'
  };
  backdrop-filter: ${props => props.$scrolled ? 'blur(10px)' : 'none'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const Logo = styled(Link)`
  font-size: 1.8rem;
  font-weight: bold;
  color: #E50914;
  text-decoration: none;
  font-family: 'Netflix Sans', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const Navigation = styled.nav`
  display: flex;
  gap: 1.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.$active ? 'white' : '#e5e5e5'};
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  transition: color 0.3s ease;
  position: relative;

  &:hover {
    color: #b3b3b3;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: ${props => props.$active ? '100%' : '0'};
    height: 2px;
    background-color: #E50914;
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
`;

const SearchIcon = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;

  &:hover {
    color: #b3b3b3;
  }
`;

const SearchInput = styled(motion.input)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid white;
  color: white;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  width: 250px;
  border-radius: 2px;

  &::placeholder {
    color: #b3b3b3;
  }

  &:focus {
    outline: none;
    border-color: #E50914;
  }

  @media (max-width: 768px) {
    width: 200px;
  }
`;

const NotificationIcon = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.3s ease;

  &:hover {
    color: #b3b3b3;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ProfileDropdown = styled.div`
  position: relative;
`;

const ProfileButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
`;

const ProfileAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
`;

const DropdownArrow = styled.span`
  color: white;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid #333;
  border-radius: 4px;
  min-width: 160px;
  padding: 0.5rem 0;
  margin-top: 0.5rem;
`;

const DropdownItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  text-align: left;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { currentProfile } = useSelector(state => state.user);
  const { searchQuery } = useSelector(state => state.content);

  const [scrolled, setScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  const handleSearchToggle = () => {
    setShowSearch(!showSearch);
    if (showSearch && localSearchQuery) {
      handleSearch();
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    setShowAutocomplete(value.length >= 2);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSearch = () => {
    if (localSearchQuery.trim()) {
      dispatch(setSearchQuery(localSearchQuery));
      navigate('/search');
    }
    setShowSearch(false);
  };

  const handleAutocompleteSelect = (movie) => {
    setLocalSearchQuery(movie.title);
    setShowAutocomplete(false);
    setShowSearch(false);
  };

  const handleAutocompleteClose = () => {
    setShowAutocomplete(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    setShowDropdown(false);
  };

  const navigationItems = [
    { path: '/', label: 'Home' },
    { path: '/browse', label: 'TV Shows' },
    { path: '/browse', label: 'Movies' },
    { path: '/browse', label: 'New & Popular' },
    { path: '/my-list', label: 'My List' },
    { path: '/settings', label: 'Settings' },
    { path: '/debug', label: 'Debug' },
  ];

  return (
    <HeaderContainer
      $scrolled={scrolled}
      initial={{ y: -70 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <LeftSection>
        <Logo to="/" aria-label="Netflix Home">
          NETFLIX
        </Logo>

        <Navigation role="navigation" aria-label="Main navigation">
          {navigationItems.map((item) => (
            <NavLink
              key={item.path + item.label}
              to={item.path}
              $active={location.pathname === item.path}
            >
              {item.label}
            </NavLink>
          ))}
        </Navigation>

        <MobileMenuButton aria-label="Open mobile menu">
          ☰
        </MobileMenuButton>
      </LeftSection>

      <RightSection>
        <SearchContainer>
          <SearchIcon
            onClick={handleSearchToggle}
            aria-label="Search"
            aria-expanded={showSearch}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </SearchIcon>

          {showSearch && (
            <SearchInput
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 250, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              type="text"
              placeholder="Titles, people, genres"
              value={localSearchQuery}
              onChange={handleSearchChange}
              onBlur={() => {
                if (!localSearchQuery && !showAutocomplete) {
                  setShowSearch(false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit(e);
                } else if (e.key === 'Escape') {
                  setShowSearch(false);
                }
              }}
              autoFocus
            />
          )}
          
          <SearchAutocomplete
            query={localSearchQuery}
            isVisible={showSearch && showAutocomplete}
            onSelect={handleAutocompleteSelect}
            onClose={handleAutocompleteClose}
          />
        </SearchContainer>

        <NotificationIcon aria-label="Notifications">
          🔔
        </NotificationIcon>

        <ProfileDropdown>
          <ProfileButton
            onClick={() => setShowDropdown(!showDropdown)}
            aria-label="Profile menu"
            aria-expanded={showDropdown}
          >
            <ProfileAvatar
              src={currentProfile?.avatar}
              alt={`${currentProfile?.name} profile`}
            />
            <DropdownArrow $isOpen={showDropdown}>▼</DropdownArrow>
          </ProfileButton>

          {showDropdown && (
            <DropdownMenu
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <DropdownItem onClick={() => setShowDropdown(false)}>
                Manage Profiles
              </DropdownItem>
              <DropdownItem onClick={() => setShowDropdown(false)}>
                Account
              </DropdownItem>
              <DropdownItem onClick={() => setShowDropdown(false)}>
                Help Center
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                Sign out of Netflix
              </DropdownItem>
            </DropdownMenu>
          )}
        </ProfileDropdown>
      </RightSection>
    </HeaderContainer>
  );
};

export default Header;